import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { domainKeys } from "~/components/siteSpeed/queryKeys";
import { useVisibilityEngineRoutes } from "~/components/siteSpeed/useVisibilityEngineRoutes";
import { useWizard } from "~/contexts/WizardContext.tsx";
import { getBrandName } from "~/utils/white-label";

import { apiClient } from "../../../api/ApiClient";
import { Icon } from "../../../assets/icons/Icon";
import styles from "./IsNoSetup.module.scss";

type IntegrationType = {
  integration_type: "full" | "partial" | "edge" | "plugin";
};

const IsNoSetup = () => {
  const { domainId } = useParams();
  const { domain } = useWizard();
  const navigate = useNavigate();
  const veRoutes = useVisibilityEngineRoutes();
  const queryClient = useQueryClient();
  const queryKey = domainKeys.detail(domainId!);
  const { mutate: updateDomain, isPending } = useMutation<
    unknown,
    unknown,
    IntegrationType
  >({
    mutationFn: async (data: IntegrationType) => {
      await apiClient.domains.domainsPartialUpdate(Number(domainId), {
        ...data,
      });
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey });

      enqueueSnackbar("Domain updated successfully", {
        variant: "success",
      });
    },
    //@ts-expect-error BE types
    onError: ({ error }) => {
      const errorMessage = error?.error?.message || "Error updating domain";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
  });

  const handleEasyIntegration = () => {
    updateDomain({ integration_type: "partial" });
  };

  const handleFullIntegration = () => {
    updateDomain({ integration_type: "full" });
  };

  const handleEdgeIntegration = () => {
    updateDomain({ integration_type: "edge" });
  };

  const handlePluginIntegration = () => {
    updateDomain({ integration_type: "plugin" });
  };

  useEffect(() => {
    if (!domain?.integration_type || !domainId) return;

    // After integration type is selected,
    // always go to the domain root.
    // Wizard route guard will handle the correct redirect.
    navigate(veRoutes.domain(domainId), {
      replace: true,
    });
  }, [domain?.integration_type, domainId, navigate, veRoutes]);

  return (
    <div className={styles.isNoSetup}>
      {/*
       // @ts-expect-error TS2339 non-existing className */}
      <div className={styles.isNoSetupHeader}>
        <div className={styles.title}>{domain?.name}</div>
        <p>
          Adjust the setting of your domain aka website here. Boost your website
          now!
        </p>
      </div>
      <div className={styles.isNoSetupSection}>
        <Icon
          iconName="NeedSetupIcon"
          // @ts-expect-error TS2339 non-existing className
          className={styles.icon}
          width="14rem"
          height="8rem"
        />
        <div className={styles.title}>Configuration Required</div>
        <p>
          Seems like your domain hasn`t been set up yet. Follow our instructions
          and open new opportunities of the site optimization!
        </p>
      </div>
      <div className={styles.isNoSetupButtonsSection}>
        <div className={styles.isNoSetupButtons}>
          <div
            className={clsx(styles.isNoSetupButton, {
              [styles.isNoSetupButtonDisabled]: isPending,
            })}
            onClick={isPending ? undefined : handleEasyIntegration}
          >
            {/*
             // @ts-expect-error TS2551 non-existing className */}
            <div className={styles.isNoSetupButtonDescription}>
              <div className={styles.isNoSetupButtonTitle}>
                Easy Integration
              </div>
              <div className={styles.isNoSetupButtonDescriptionText}>
                Install AI Visibility Engine by changing DNS records and copying
                settings to {getBrandName()}. Only provides speed optimization
                for one subdomain. You can always switch to full coverage later.
              </div>
            </div>
            <Icon iconName="ArrowRightIcon" />
          </div>

          <div
            className={clsx(styles.isNoSetupButton, {
              [styles.isNoSetupButtonDisabled]: isPending,
            })}
            onClick={isPending ? undefined : handleFullIntegration}
          >
            {/*
             // @ts-expect-error TS2551 non-existing className */}
            <div className={styles.isNoSetupButtonDescription}>
              <div className={styles.isNoSetupButtonTitle}>
                Full Integration
              </div>
              <div className={styles.isNoSetupButtonDescriptionText}>
                Make {getBrandName()} your authoritative DNS provider. Requires
                transfer of DNS settings to {getBrandName()} and nameservers
                change. Recommended for users who need to cover multiple
                subdomains.
              </div>
            </div>
            <Icon iconName="ArrowRightIcon" />
          </div>

          <div
            className={clsx(styles.isNoSetupButton, {
              [styles.isNoSetupButtonDisabled]: isPending,
            })}
            onClick={isPending ? undefined : handleEdgeIntegration}
          >
            {/*
             // @ts-expect-error TS2551 non-existing className */}
            <div className={styles.isNoSetupButtonDescription}>
              <div className={styles.isNoSetupButtonTitle}>
                Edge Integration
              </div>
              <div className={styles.isNoSetupButtonDescriptionText}>
                Use {getBrandName()} as an edge caching and optimization layer
                by integrating it with edge compute platforms such as Cloudflare
                Workers.
              </div>
            </div>
            <Icon iconName="ArrowRightIcon" />
          </div>

          <div
            className={clsx(styles.isNoSetupButton, {
              [styles.isNoSetupButtonDisabled]: isPending,
            })}
            onClick={isPending ? undefined : handlePluginIntegration}
          >
            {/*
             // @ts-expect-error TS2551 non-existing className */}
            <div className={styles.isNoSetupButtonDescription}>
              <div className={styles.isNoSetupButtonTitle}>
                Plugin Integration
              </div>
              <div className={styles.isNoSetupButtonDescriptionText}>
                Install the WordPress plugin to enable AI Visibility Engine on
                your site. No DNS changes required.
              </div>
            </div>
            <Icon iconName="ArrowRightIcon" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IsNoSetup;
